import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { TabView, TabPanel } from 'primereact/tabview';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function MachineAudit() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [globalFilter, setGlobalFilter] = useState('');
    const [signingIdFilter, setSigningIdFilter] = useState('');
    const [machineNameFilter, setMachineNameFilter] = useState('');
    const [empIdFilter, setEmpIdFilter] = useState('');
    const toast = useRef(null);
    const fileUploadRef = useRef(null);

    // Bulk enrollment state
    const [editRows, setEditRows] = useState({});
    const [selectedEmpIds, setSelectedEmpIds] = useState({});
    const [teacherTypes, setTeacherTypes] = useState([]);
    const [regions, setRegions] = useState([]);
    const [enrolling, setEnrolling] = useState(false);

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
    });

    const showToast = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 4000 });
    };

    const handleToggleServerStatus = (rowData) => {
        if (!rowData?.teacherId) return;

        const newStatusLabel = rowData.teacherActive === 1 ? 'Inactive' : 'Active';
        const name = (rowData.serverName || '').trim() || `emp_id ${rowData.empId}`;

        confirmDialog({
            message: `Set "${name}" to ${newStatusLabel}?`,
            header: `Confirm ${newStatusLabel}`,
            icon: rowData.teacherActive === 1 ? 'pi pi-ban' : 'pi pi-check-circle',
            acceptClassName: rowData.teacherActive === 1 ? 'p-button-warning' : 'p-button-success',
            accept: async () => {
                try {
                    const response = await axios.post(
                        `${API_BASE_URL}/enrollment-sync/toggle-status`,
                        { teacherId: rowData.teacherId },
                        getAuthHeaders()
                    );

                    if (response.data.success) {
                        const newTeacherActive = rowData.teacherActive === 1 ? 0 : 1;

                        setResult(prev => {
                            if (!prev?.data) return prev;
                            const updateRow = (r) => (r && r.empId === rowData.empId ? { ...r, teacherActive: newTeacherActive } : r);
                            return {
                                ...prev,
                                data: {
                                    ...prev.data,
                                    matched: (prev.data.matched || []).map(updateRow),
                                    statusMismatch: (prev.data.statusMismatch || []).map(updateRow),
                                    noSigningId: (prev.data.noSigningId || []).map(updateRow),
                                    serverOnly: (prev.data.serverOnly || []).map(updateRow),
                                    allMachineUsers: (prev.data.allMachineUsers || []).map(updateRow)
                                }
                            };
                        });

                        showToast('success', 'Status Changed', response.data.message);
                    }
                } catch (error) {
                    const msg = error.response?.data?.message || error.message;
                    showToast('error', 'Toggle Failed', msg);
                }
            }
        });
    };

    // Fetch lookup data for dropdowns
    const fetchLookups = useCallback(async () => {
        try {
            const [ttRes, rgRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/enrollment-sync/teacher-types`, getAuthHeaders()),
                axios.get(`${API_BASE_URL}/enrollment-sync/regions`, getAuthHeaders())
            ]);
            if (ttRes.data.success) setTeacherTypes(ttRes.data.data);
            if (rgRes.data.success) setRegions(rgRes.data.data);
        } catch (err) {
            console.error('Failed to fetch lookups:', err);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { fetchLookups(); }, [fetchLookups]);

    // Initialize editable rows when result changes
    useEffect(() => {
        if (result?.data?.machineOnly) {
            const rows = {};
            for (const m of result.data.machineOnly) {
                rows[m.empId] = { signingId: '', firstName: '', lastName: '', teacherTypeId: null, regionId: null, nic: '' };
            }
            setEditRows(rows);
            setSelectedEmpIds({});
        }
    }, [result]);

    const updateEditRow = (empId, field, value) => {
        setEditRows(prev => ({ ...prev, [empId]: { ...prev[empId], [field]: value } }));
    };

    const isRowComplete = (empId) => {
        const r = editRows[empId];
        return r && r.firstName && r.lastName && r.teacherTypeId && r.regionId;
    };

    const handleBulkEnroll = async () => {
        const selected = Object.keys(selectedEmpIds).filter(id => selectedEmpIds[id] && isRowComplete(Number(id)));
        if (selected.length === 0) {
            showToast('warn', 'No Valid Rows', 'Select rows with all required fields filled (First Name, Last Name, Type, Region)');
            return;
        }
        setEnrolling(true);
        try {
            const entries = selected.map(id => {
                const eid = Number(id);
                const r = editRows[eid];
                return { empId: eid, signingId: r.signingId || null, firstName: r.firstName, lastName: r.lastName, teacherTypeId: r.teacherTypeId, regionId: r.regionId, nic: r.nic || null };
            });
            const res = await axios.post(`${API_BASE_URL}/enrollment-sync/bulk-register`, { entries }, getAuthHeaders());
            if (res.data.registered?.length > 0) {
                showToast('success', 'Enrollment Complete', `Registered ${res.data.registered.length} users`);
                // Remove registered emp_ids from machineOnly
                const regIds = new Set(res.data.registered.map(r => r.empId));
                setResult(prev => ({
                    ...prev,
                    data: { ...prev.data, machineOnly: prev.data.machineOnly.filter(m => !regIds.has(m.empId)) },
                    summary: { ...prev.summary, machineOnly: prev.summary.machineOnly - res.data.registered.length, matched: prev.summary.matched + res.data.registered.length }
                }));
            }
            if (res.data.errors?.length > 0) {
                showToast('warn', 'Some Errors', res.data.errors.map(e => `emp_id ${e.empId}: ${e.reason}`).join('\n'));
            }
        } catch (error) {
            showToast('error', 'Enrollment Failed', error.response?.data?.message || error.message);
        } finally {
            setEnrolling(false);
        }
    };

    const handleUpload = async (event) => {
        const file = event.files[0];
        if (!file) return;

        if (!file.name.endsWith('.dat')) {
            showToast('warn', 'Invalid File', 'Please upload a .dat file from the ZKTeco machine');
            fileUploadRef.current?.clear();
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('datFile', file);

            const response = await axios.post(
                `${API_BASE_URL}/machine-audit/compare`,
                formData,
                {
                    ...getAuthHeaders(),
                    headers: {
                        ...getAuthHeaders().headers,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                setResult(response.data);
                showToast('success', 'Analysis Complete',
                    `Processed ${response.data.summary.machineTotal} machine records vs ${response.data.summary.serverTotal} server records`);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                showToast('error', 'Session Expired', 'Your session has expired. Redirecting to login...');
                setTimeout(() => { window.location.href = '/admin'; }, 1500);
                return;
            }
            const msg = error.response?.data?.message || error.message;
            showToast('error', 'Upload Failed', msg);
        } finally {
            setLoading(false);
            fileUploadRef.current?.clear();
        }
    };

    const statusBody = (rowData, field) => {
        const val = rowData[field];
        if (val === 1) return <Tag value="Active" severity="success" />;
        if (val === 0) return <Tag value="Inactive" severity="danger" />;
        return <Tag value="N/A" severity="warning" />;
    };

    const lastPunchDateBody = (rowData) => {
        const v = rowData?.lastPunchDate;
        if (!v) return <span style={{ color: '#999' }}>-</span>;
        const d = v instanceof Date ? v : new Date(v);
        if (Number.isNaN(d.getTime())) return <span style={{ color: '#999' }}>-</span>;
        const yyyyMmDd = d.toISOString().slice(0, 10);
        return <span>{yyyyMmDd}</span>;
    };

    const serverStatusBody = (rowData) => {
        if (!rowData?.teacherId) {
            return <Button label="N/A" className="p-button-sm p-button-secondary" disabled />;
        }
        return rowData.teacherActive === 1
            ? (
                <Button
                    label="Active"
                    icon="pi pi-check"
                    className="p-button-sm p-button-success"
                    onClick={() => handleToggleServerStatus(rowData)}
                />
            )
            : (
                <Button
                    label="Inactive"
                    icon="pi pi-ban"
                    className="p-button-sm p-button-danger"
                    onClick={() => handleToggleServerStatus(rowData)}
                />
            );
    };

    const issueBody = (rowData) => {
        if (!rowData.issue) return null;
        const isServerActive = rowData.issue.includes('Active on server');
        return (
            <Tag
                value={rowData.issue}
                severity={isServerActive ? 'warning' : 'danger'}
                style={{ fontSize: '0.75rem' }}
            />
        );
    };

    const signingIdBody = (rowData) => {
        if (!rowData.signingId) return <Tag value="Not Assigned" severity="danger" style={{ fontSize: '0.75rem' }} />;
        return <span style={{ fontWeight: 'bold' }}>{rowData.signingId}</span>;
    };

    const filterHeader = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
            <span className="p-input-icon-left" style={{ width: '150px' }}>
                <i className="pi pi-hashtag" />
                <InputText
                    value={empIdFilter}
                    onChange={(e) => setEmpIdFilter(e.target.value)}
                    placeholder="emp_id..."
                    style={{ width: '100%' }}
                />
            </span>
            <span className="p-input-icon-left" style={{ width: '180px' }}>
                <i className="pi pi-id-card" />
                <InputText
                    value={signingIdFilter}
                    onChange={(e) => setSigningIdFilter(e.target.value)}
                    placeholder="Signing ID..."
                    style={{ width: '100%' }}
                />
            </span>
            <span className="p-input-icon-left" style={{ width: '200px' }}>
                <i className="pi pi-desktop" />
                <InputText
                    value={machineNameFilter}
                    onChange={(e) => setMachineNameFilter(e.target.value)}
                    placeholder="Machine Name..."
                    style={{ width: '100%' }}
                />
            </span>
            <span className="p-input-icon-left" style={{ width: '200px' }}>
                <i className="pi pi-search" />
                <InputText
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder="Search all..."
                    style={{ width: '100%' }}
                />
            </span>
        </div>
    );

    const filterData = useCallback((data) => {
        if (!data) return [];
        return data.filter(row => {
            if (empIdFilter && !String(row.empId || '').toLowerCase().includes(empIdFilter.toLowerCase())) return false;
            if (signingIdFilter && !String(row.signingId || '').toLowerCase().includes(signingIdFilter.toLowerCase())) return false;
            if (machineNameFilter && !String(row.machineName || '').toLowerCase().includes(machineNameFilter.toLowerCase())) return false;
            return true;
        });
    }, [empIdFilter, signingIdFilter, machineNameFilter]);

    const cardClick = (tabIndex) => { setActiveTab(tabIndex); setGlobalFilter(''); setEmpIdFilter(''); setSigningIdFilter(''); setMachineNameFilter(''); };
    const cardStyle = (bg, border, isActive) => ({
        textAlign: 'center', background: bg, border: `2px solid ${border}`,
        cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
        transform: isActive ? 'scale(1.04)' : 'scale(1)',
        boxShadow: isActive ? `0 4px 12px ${border}80` : 'none'
    });

    const summaryCards = result ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <Card style={cardStyle('#f0fdf4', '#bbf7d0', activeTab === 4)} onClick={() => cardClick(4)}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#16a34a' }}>{result.summary.matched}</div>
                <div style={{ fontSize: '0.85rem', color: '#166534' }}>Matched</div>
            </Card>
            <Card style={cardStyle('#fef9c3', '#fde68a', activeTab === 0)} onClick={() => cardClick(0)}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ca8a04' }}>{result.summary.statusMismatch}</div>
                <div style={{ fontSize: '0.85rem', color: '#854d0e' }}>Status Mismatch</div>
            </Card>
            <Card style={cardStyle('#fce4ec', '#f48fb1', activeTab === 1)} onClick={() => cardClick(1)}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#c62828' }}>{result.summary.noSigningId}</div>
                <div style={{ fontSize: '0.85rem', color: '#b71c1c' }}>No Signing ID</div>
            </Card>
            <Card style={cardStyle('#fff3e0', '#ffcc80', activeTab === 2)} onClick={() => cardClick(2)}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#e65100' }}>{result.summary.machineOnly}</div>
                <div style={{ fontSize: '0.85rem', color: '#bf360c' }}>Machine Only</div>
            </Card>
            <Card style={cardStyle('#e3f2fd', '#90caf9', activeTab === 3)} onClick={() => cardClick(3)}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1565c0' }}>{result.summary.serverOnly}</div>
                <div style={{ fontSize: '0.85rem', color: '#0d47a1' }}>Server Only</div>
            </Card>
            <Card style={cardStyle('#f3e5f5', '#ce93d8', activeTab === 5)} onClick={() => cardClick(5)}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7b1fa2' }}>{result.summary.machineTotal}</div>
                <div style={{ fontSize: '0.85rem', color: '#4a148c' }}>All Machine Users</div>
            </Card>
        </div>
    ) : null;

    return (
        <div>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div style={{
                display: 'flex', alignItems: 'center', gap: '16px',
                marginBottom: '20px', flexWrap: 'wrap'
            }}>
                <FileUpload
                    ref={fileUploadRef}
                    mode="basic"
                    accept=".dat"
                    maxFileSize={5000000}
                    customUpload
                    uploadHandler={handleUpload}
                    auto
                    chooseLabel="Upload Machine .dat File"
                    chooseOptions={{ icon: 'pi pi-upload', className: 'p-button-outlined' }}
                />
                {loading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="4" />
                        <span style={{ color: '#666' }}>Analyzing...</span>
                    </div>
                )}
                {result && (
                    <span style={{ color: '#888', fontSize: '0.85rem' }}>
                        Machine: <b>{result.summary.machineTotal}</b> users &middot; Server: <b>{result.summary.serverTotal}</b> enrollments
                    </span>
                )}
            </div>

            {summaryCards}

            {result && (
                <TabView activeIndex={activeTab} onTabChange={(e) => { setActiveTab(e.index); setGlobalFilter(''); setEmpIdFilter(''); setSigningIdFilter(''); setMachineNameFilter(''); }}>
                    {/* Status Mismatch Tab */}
                    <TabPanel
                        header={`Status Mismatch (${result.data.statusMismatch.length})`}
                        leftIcon="pi pi-exclamation-triangle mr-2"
                    >
                        {filterHeader}
                        <DataTable
                            value={filterData(result.data.statusMismatch)}
                            paginator={true} paginatorPosition="bottom" rows={10} rowsPerPageOptions={[10, 15, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="{first} to {last} of {totalRecords}"
                            globalFilter={globalFilter}
                            emptyMessage="No status mismatches found"
                            sortField="empId" sortOrder={1}
                            size="small" stripedRows
                            
                            tableStyle={{ fontSize: '0.85rem' }}
                            style={{ marginTop: '10px' }}
                        >
                            <Column field="empId" header="emp_id" sortable style={{ width: '80px' }} />
                            <Column field="signingId" header="Signing ID" body={signingIdBody} sortable style={{ width: '100px' }} />
                            <Column field="serverName" header="Server Name" sortable />
                            <Column field="machineName" header="Machine Name" sortable />
                            <Column field="teacherActive" header="Server Status" body={serverStatusBody} sortable style={{ width: '110px' }} />
                            <Column field="machineEnabled" header="Machine Status" body={(r) => statusBody(r, 'machineEnabled')} sortable style={{ width: '110px' }} />
                            <Column field="issue" header="Issue" body={issueBody} style={{ width: '220px' }} />
                            <Column field="nic" header="NIC" sortable style={{ width: '150px' }} />
                            <Column field="punchCount" header="Punches" sortable style={{ width: '80px' }} />
                            <Column field="lastPunchDate" header="Last Punch Date" body={lastPunchDateBody} sortable style={{ width: '130px' }} />
                        </DataTable>
                    </TabPanel>

                    {/* No Signing ID Tab */}
                    <TabPanel
                        header={`No Signing ID (${result.data.noSigningId.length})`}
                        leftIcon="pi pi-id-card mr-2"
                    >
                        {filterHeader}
                        <DataTable
                            value={filterData(result.data.noSigningId)}
                            paginator={true} paginatorPosition="bottom" rows={10} rowsPerPageOptions={[10, 15, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="{first} to {last} of {totalRecords}"
                            globalFilter={globalFilter}
                            emptyMessage="All enrolled users have a Signing ID"
                            sortField="empId" sortOrder={1}
                            size="small" stripedRows
                            
                            tableStyle={{ fontSize: '0.85rem' }}
                            style={{ marginTop: '10px' }}
                        >
                            <Column field="empId" header="emp_id" sortable style={{ width: '80px' }} />
                            <Column field="serverName" header="Server Name" sortable />
                            <Column field="machineName" header="Machine Name" sortable />
                            <Column field="teacherActive" header="Server Status" body={serverStatusBody} sortable style={{ width: '110px' }} />
                            <Column field="machineEnabled" header="Machine Status" body={(r) => statusBody(r, 'machineEnabled')} sortable style={{ width: '110px' }} />
                            <Column field="nic" header="NIC" sortable style={{ width: '150px' }} />
                            <Column field="regionName" header="Region" sortable style={{ width: '130px' }} />
                            <Column field="punchCount" header="Punches" sortable style={{ width: '80px' }} />
                            <Column field="lastPunchDate" header="Last Punch Date" body={lastPunchDateBody} sortable style={{ width: '130px' }} />
                        </DataTable>
                    </TabPanel>

                    {/* Machine Only Tab */}
                    <TabPanel
                        header={`Machine Only (${result.data.machineOnly.length})`}
                        leftIcon="pi pi-desktop mr-2"
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Button
                                    label={enrolling ? 'Enrolling...' : `Enroll Selected (${Object.keys(selectedEmpIds).filter(id => selectedEmpIds[id] && isRowComplete(Number(id))).length})`}
                                    icon={enrolling ? 'pi pi-spin pi-spinner' : 'pi pi-user-plus'}
                                    className="p-button-success p-button-sm"
                                    onClick={handleBulkEnroll}
                                    disabled={enrolling || Object.keys(selectedEmpIds).filter(id => selectedEmpIds[id] && isRowComplete(Number(id))).length === 0}
                                />
                                <span style={{ fontSize: '0.8rem', color: '#888' }}>Fill First Name, Last Name, Type & Region, then select & enroll</span>
                            </div>
                            <span className="p-input-icon-left" style={{ width: '250px' }}>
                                <i className="pi pi-search" />
                                <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." style={{ width: '100%' }} />
                            </span>
                        </div>
                        <DataTable
                            value={filterData(result.data.machineOnly)}
                            paginator={true} paginatorPosition="bottom" rows={10} rowsPerPageOptions={[10, 15, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="{first} to {last} of {totalRecords}"
                            globalFilter={globalFilter}
                            emptyMessage="All machine users are enrolled on the server"
                            sortField="empId" sortOrder={1}
                            size="small" stripedRows
                            tableStyle={{ fontSize: '0.85rem' }}
                            style={{ marginTop: '10px' }}
                        >
                            <Column header="" style={{ width: '40px' }} body={(rowData) => (
                                <Checkbox
                                    checked={!!selectedEmpIds[rowData.empId]}
                                    onChange={(e) => setSelectedEmpIds(prev => ({ ...prev, [rowData.empId]: e.checked }))}
                                    disabled={!isRowComplete(rowData.empId)}
                                />
                            )} />
                            <Column field="empId" header="emp_id" sortable style={{ width: '70px' }} />
                            <Column field="machineName" header="Machine Name" sortable style={{ width: '120px' }} />
                            <Column header="Signing ID" style={{ width: '90px' }} body={(rowData) => (
                                <InputText
                                    value={editRows[rowData.empId]?.signingId || ''}
                                    onChange={(e) => updateEditRow(rowData.empId, 'signingId', e.target.value)}
                                    placeholder="SID"
                                    style={{ width: '100%', padding: '4px 6px', fontSize: '0.8rem' }}
                                />
                            )} />
                            <Column header="First Name *" style={{ width: '120px' }} body={(rowData) => (
                                <InputText
                                    value={editRows[rowData.empId]?.firstName || ''}
                                    onChange={(e) => updateEditRow(rowData.empId, 'firstName', e.target.value)}
                                    placeholder="First Name"
                                    style={{ width: '100%', padding: '4px 6px', fontSize: '0.8rem' }}
                                />
                            )} />
                            <Column header="Last Name *" style={{ width: '120px' }} body={(rowData) => (
                                <InputText
                                    value={editRows[rowData.empId]?.lastName || ''}
                                    onChange={(e) => updateEditRow(rowData.empId, 'lastName', e.target.value)}
                                    placeholder="Last Name"
                                    style={{ width: '100%', padding: '4px 6px', fontSize: '0.8rem' }}
                                />
                            )} />
                            <Column header="Type *" style={{ width: '130px' }} body={(rowData) => (
                                <Dropdown
                                    value={editRows[rowData.empId]?.teacherTypeId || null}
                                    options={teacherTypes}
                                    optionLabel="name"
                                    optionValue="id"
                                    onChange={(e) => updateEditRow(rowData.empId, 'teacherTypeId', e.value)}
                                    placeholder="Type"
                                    style={{ width: '100%', fontSize: '0.8rem' }}
                                    className="p-inputtext-sm"
                                />
                            )} />
                            <Column header="Region *" style={{ width: '150px' }} body={(rowData) => (
                                <Dropdown
                                    value={editRows[rowData.empId]?.regionId || null}
                                    options={regions}
                                    optionLabel="name"
                                    optionValue="id"
                                    onChange={(e) => updateEditRow(rowData.empId, 'regionId', e.value)}
                                    placeholder="Region"
                                    style={{ width: '100%', fontSize: '0.8rem' }}
                                    className="p-inputtext-sm"
                                />
                            )} />
                            <Column header="NIC" style={{ width: '120px' }} body={(rowData) => (
                                <InputText
                                    value={editRows[rowData.empId]?.nic || ''}
                                    onChange={(e) => updateEditRow(rowData.empId, 'nic', e.target.value)}
                                    placeholder="NIC"
                                    style={{ width: '100%', padding: '4px 6px', fontSize: '0.8rem' }}
                                />
                            )} />
                            <Column field="machineEnabled" header="Status" body={(r) => statusBody(r, 'machineEnabled')} style={{ width: '80px' }} />
                            <Column field="punchCount" header="Punches" sortable style={{ width: '70px' }} />
                            <Column field="lastPunchDate" header="Last Punch Date" body={lastPunchDateBody} sortable style={{ width: '130px' }} />
                        </DataTable>
                    </TabPanel>

                    {/* Server Only Tab */}
                    <TabPanel
                        header={`Server Only (${result.data.serverOnly.length})`}
                        leftIcon="pi pi-server mr-2"
                    >
                        {filterHeader}
                        <DataTable
                            value={filterData(result.data.serverOnly)}
                            paginator={true} paginatorPosition="bottom" rows={10} rowsPerPageOptions={[10, 15, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="{first} to {last} of {totalRecords}"
                            globalFilter={globalFilter}
                            emptyMessage="All server enrollments exist in the machine"
                            sortField="empId" sortOrder={1}
                            size="small" stripedRows
                            
                            tableStyle={{ fontSize: '0.85rem' }}
                            style={{ marginTop: '10px' }}
                        >
                            <Column field="empId" header="emp_id" sortable style={{ width: '80px' }} />
                            <Column field="signingId" header="Signing ID" body={signingIdBody} sortable style={{ width: '100px' }} />
                            <Column field="serverName" header="Server Name" sortable />
                            <Column field="teacherActive" header="Server Status" body={serverStatusBody} sortable style={{ width: '110px' }} />
                            <Column field="nic" header="NIC" sortable style={{ width: '150px' }} />
                            <Column field="teacherTypeName" header="Type" sortable style={{ width: '130px' }} />
                            <Column field="punchCount" header="Punches" sortable style={{ width: '80px' }} />
                            <Column field="lastPunchDate" header="Last Punch Date" body={lastPunchDateBody} sortable style={{ width: '130px' }} />
                        </DataTable>
                    </TabPanel>

                    {/* All Matched Tab */}
                    <TabPanel
                        header={`Matched (${result.data.matched.length})`}
                        leftIcon="pi pi-check-circle mr-2"
                    >
                        {filterHeader}
                        <DataTable
                            value={filterData(result.data.matched)}
                            paginator={true} paginatorPosition="bottom" rows={10} rowsPerPageOptions={[10, 15, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="{first} to {last} of {totalRecords}"
                            globalFilter={globalFilter}
                            emptyMessage="No matched records"
                            sortField="signingId" sortOrder={1}
                            size="small" stripedRows
                            
                            tableStyle={{ fontSize: '0.85rem' }}
                            style={{ marginTop: '10px' }}
                        >
                            <Column field="empId" header="emp_id" sortable style={{ width: '80px' }} />
                            <Column field="signingId" header="Signing ID" body={signingIdBody} sortable style={{ width: '100px' }} />
                            <Column field="serverName" header="Server Name" sortable />
                            <Column field="machineName" header="Machine Name" sortable />
                            <Column field="teacherActive" header="Server Status" body={serverStatusBody} sortable style={{ width: '110px' }} />
                            <Column field="machineEnabled" header="Machine Status" body={(r) => statusBody(r, 'machineEnabled')} sortable style={{ width: '110px' }} />
                            <Column field="nic" header="NIC" sortable style={{ width: '150px' }} />
                            <Column field="teacherTypeName" header="Type" sortable style={{ width: '130px' }} />
                            <Column field="punchCount" header="Punches" sortable style={{ width: '80px' }} />
                            <Column field="lastPunchDate" header="Last Punch Date" body={lastPunchDateBody} sortable style={{ width: '130px' }} />
                        </DataTable>
                    </TabPanel>
                    {/* All Machine Users Tab */}
                    <TabPanel
                        header={`All Machine Users (${result.data.allMachineUsers?.length || result.summary.machineTotal})`}
                        leftIcon="pi pi-users mr-2"
                    >
                        {filterHeader}
                        <DataTable
                            value={filterData(result.data.allMachineUsers || [])}
                            paginator={true} paginatorPosition="bottom" rows={10} rowsPerPageOptions={[10, 15, 25, 50, 100]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="{first} to {last} of {totalRecords}"
                            globalFilter={globalFilter}
                            emptyMessage="No machine users found"
                            sortField="empId" sortOrder={1}
                            size="small" stripedRows
                            tableStyle={{ fontSize: '0.85rem' }}
                            style={{ marginTop: '10px' }}
                        >
                            <Column field="empId" header="emp_id" sortable style={{ width: '80px' }} />
                            <Column field="machineName" header="Machine Name" sortable />
                            <Column field="machineEnabled" header="Machine Status" body={(r) => statusBody(r, 'machineEnabled')} sortable style={{ width: '110px' }} />
                            <Column field="serverName" header="Server Name" sortable />
                            <Column field="signingId" header="Signing ID" body={signingIdBody} sortable style={{ width: '100px' }} />
                            <Column field="teacherActive" header="Server Status" body={serverStatusBody} sortable style={{ width: '110px' }} />
                            <Column field="category" header="Category" sortable style={{ width: '140px' }} body={(r) => {
                                const colors = { 'Matched': 'success', 'Status Mismatch': 'warning', 'No Signing ID': 'danger', 'Machine Only': null };
                                return <Tag value={r.category} severity={colors[r.category]} style={{ fontSize: '0.75rem' }} />;
                            }} />
                            <Column field="punchCount" header="Punches" sortable style={{ width: '80px' }} />
                            <Column field="lastPunchDate" header="Last Punch Date" body={lastPunchDateBody} sortable style={{ width: '130px' }} />
                        </DataTable>
                    </TabPanel>
                </TabView>
            )}

            {!result && !loading && (
                <div style={{
                    textAlign: 'center', padding: '60px 20px', color: '#999',
                    border: '2px dashed #ddd', borderRadius: '12px', marginTop: '20px'
                }}>
                    <i className="pi pi-upload" style={{ fontSize: '3rem', marginBottom: '16px', display: 'block' }}></i>
                    <h3 style={{ margin: '0 0 8px', color: '#666' }}>Upload Machine User Data</h3>
                    <p style={{ margin: 0 }}>
                        Upload the <code>user.dat</code> file exported from the ZKTeco biometric machine.<br />
                        The system will compare it with server enrollment data and identify discrepancies.
                    </p>
                </div>
            )}
        </div>
    );
}

export default MachineAudit;
